import { openDialog, openPanel } from './dialog.js';
import { dayKey } from '../tasks.js';
import { yourProjectIds } from '../project-picker-model.js';

// NEW TASK — the full form behind the toolbar button (Karan 01/09).
//
// The inline row and ⌘K quick-add are for speed: a line of text, parsed. They
// are the wrong tool for the thing this practice actually does, which is work
// on a numbered job for a named client at a real address. Karan: "I need to see
// the project no, client, site, with the option to add in what specifically I
// am doing for that."
//
// So the project picker shows the NUMBER, the CLIENT and the SITE, and the task
// says what is being done on that job. Creates are a modal, edits are a panel
// (the house dialog system, 07/08) — this is a create.

/** One line per project: the number in mono, then client — site, so the list
 *  can be read by number or by place, which is how people actually look. */
export function projectOptions(projects = []) {
  return projects
    .filter((p) => p.number || p.name)
    .sort((a, b) => String(b.number || '').localeCompare(String(a.number || '')))
    .map((p) => ({
      value: p.id,
      number: p.number || '',
      label: [p.name, p.site].filter(Boolean).join(' — '),
      consequence: p.status ? `${p.status}${p.lead ? ` · ${p.lead}` : ''}` : '',
    }));
}

/**
 * @param opts.projects  [{ id, number, name, site, status, lead }]
 * @param opts.people    the team, for the assignee picker
 * @param opts.onCreate  async (fields) => void
 * @param opts.project   pre-selected project, when opened from a record
 */
export function openNewTask({ projects = [], people = [], today = dayKey(), project = null, me = null, title = '', checkerId = '', onCreate }) {
  // Tasks v2 (design_handoff_tasks_v2): no load beside the names and no
  // estimate field — the load figure read estimates that were rarely set.

  // v4 (design p51): What needs doing → Project → For + Checked by → Due chips →
  // Notes. Priority is no longer asked (the rules still need one: normal, 2).
  return openDialog({
    title: 'New task',
    width: 520,
    note: 'Faster: type “8601 chase builder tomorrow” in the line on Tasks.',
    fields: [
      { key: 'title', label: 'What needs doing', type: 'text', span: true, required: true, value: title,
        placeholder: 'Chase builder for revised kitchen opening dimensions' },
      // The shared searchable picker (v4.6.1): the ones you lead first, then live
      // ones; typing finds any project, completed ones included.
      { key: 'projectId', label: 'Project', type: 'project', span: true, optional: true,
        value: project ? project.id : '', projects, noneLabel: 'No project (overhead)',
        suggested: me ? yourProjectIds({ projects, leadKey: me.userId }) : [],
        help: 'Leave it empty for overhead work — it can’t be billed.' },
      { key: 'assigneeId', label: 'For', type: 'select', optional: true,
        value: me ? me.userId : '',
        options: (me ? [{ value: me.userId, label: 'Me' }] : [])
          .concat([{ value: '', label: 'Nobody yet — goes to the team meeting' }])
          .concat(people.filter((p) => !me || p.userId !== me.userId).map((p) => ({ value: p.userId, label: p.name }))) },
      // checkerId prefills it (v4.19.4: the project's Checker row picks the person first).
      { key: 'checkerId', label: 'Checked by', type: 'select', optional: true, value: checkerId || '',
        options: [{ value: '', label: 'No check' }].concat(people.map((p) => ({ value: p.userId, label: p.name }))) },
      { key: 'dueDate', label: 'Due', type: 'date', span: true, optional: true,
        pills: [{ label: 'Today', date: today }, { label: 'Tomorrow', date: addDay(today, 1) }, { label: 'Next week', date: nextMonday(today) }, { label: 'No date', date: '' }] },
      { key: 'detail', label: 'Notes', type: 'textarea', span: true, optional: true,
        max: 600, placeholder: 'Anything the person picking this up should know' },
    ],
    primary: {
      label: 'Add task',
      onSubmit: async (v) => {
        const person = people.find((p) => p.userId === v.assigneeId) || null;
        await onCreate({
          title: String(v.title || '').trim(),
          description: String(v.detail || '').trim(),
          projectId: v.projectId || null,
          assigneeId: person ? person.userId : null,
          assigneeEmail: person ? (person.email || null) : null,
          checkerId: v.checkerId || null,
          dueDate: v.dueDate || null,
          priority: 2,
        });
      },
    },
  });
}

/**
 * What an edit writes: only the fields that changed, so an edit never stamps
 * over a reassignment someone else made a moment ago. Assignment is NOT here —
 * that is the panel's Reassign. Estimates are not edited any more (Tasks v2);
 * one already on a record is left as it is. Pure, for the tests.
 */
export function editPatch(task = {}, v = {}) {
  const next = {
    title: String(v.title || '').trim().slice(0, 300),
    description: String(v.detail || '').trim().slice(0, 4000),
    projectId: v.projectId || null,
    checkerId: v.checkerId || null,
    dueDate: v.dueDate || null,
    priority: Number(v.priority) || 2,
  };
  if (!next.title) throw new Error('A task needs a title.');
  const patch = {};
  for (const [k, val] of Object.entries(next)) {
    const was = task[k] == null ? null : task[k];
    if (k === 'description' ? String(was || '') !== val : was !== val) patch[k] = val;
  }
  return patch;
}

/**
 * EDIT — a panel, because edits are panels (the house dialog system). Until
 * 05/10 nothing on the Tasks page could change a task once it existed: not the
 * title, the due date or the job. Karan: "can't create/edit".
 */
export function openEditTask({ task, projects = [], people = [], onSave }) {
  return openPanel({
    title: 'Edit task',
    subtitle: task.projectRef || 'Overhead',
    fields: [
      { key: 'title', label: 'What are you doing', type: 'text', span: true, required: true, value: task.title || '' },
      { key: 'detail', label: 'Anything the person needs to know', type: 'textarea', span: true, optional: true,
        max: 600, value: task.description || '' },
      { key: 'projectId', label: 'Project', type: 'project', span: true, optional: true,
        value: task.projectId || '', projects, noneLabel: 'No project (overhead)' },
      { key: 'checkerId', label: 'Checker', type: 'select', optional: true, value: task.checkerId || '',
        options: [{ value: '', label: 'Nobody' }].concat(people.map((p) => ({ value: p.userId, label: p.name }))) },
      { key: 'dueDate', label: 'Due', type: 'date', optional: true, value: task.dueDate || '' },
      { key: 'priority', label: 'Priority', type: 'select', optional: true, value: String(task.priority || 2),
        options: [{ value: '1', label: '1 — first' }, { value: '2', label: '2 — normal' }, { value: '3', label: '3 — when it fits' }] },
    ],
    primary: {
      label: 'Save',
      onSubmit: async (v) => {
        const patch = editPatch(task, v);
        if (Object.keys(patch).length) await onSave(patch);
      },
    },
  });
}


function addDay(today, n) {
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + n);
  return dayKey(d);
}
/** Next week = the coming Monday. */
function nextMonday(today) {
  const d = new Date(`${today}T00:00:00`);
  d.setDate(d.getDate() + (((8 - d.getDay()) % 7) || 7));
  return dayKey(d);
}
